import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './style.scss';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { apiCallStatus } from 'helpers/constants';
import { TimeSelectorRow } from 'sharedComponents/TimeSelectorRow';
import { GeneralTable } from 'sharedComponents/GeneralTable';
import { TableHeaderRows } from 'sharedComponents/TableHeaderRows';
import { getPreselectedDates } from '../helpers';
import { getColumns, TIMESTAMP_TABLE_COLUMN_KEYS, SORT_DIRECTION, TYPE_CONSTANTS, UNIT_OF_MEASUREMENT, MAX_PRECISION } from './constants';
import { setMaxPrecision } from 'helpers/formatters';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { formatDateTime } from 'helpers/dateTimeFormatter';

const RawDataCollectionDialog = (props) => {
    const {
        isShowRawDataCollectionDialog,
        setShowRawDataCollectionDialog,
        setSelectedRawDataCollection,
        rawDataCollections,
        getRawDataCollectionTimestamps,
        selectedAsset,
        selectedTimestamp,
        setSelectedTimestamp,
        unitOfMeasurement
    } = props;
    const { t: translate } = useTranslation();

    const [rawDataError, setRawDataError] = useState(null);
    const headerScrollRef = useRef(null);

    const [dateSelection, setSelectedDates] = useState(getPreselectedDates(7));
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null
    });
    const [filters, setFilters] = useState({
        [TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.SPEED]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE]: ''
    });

    const responsePrecision = rawDataCollections?.data?.precision;
    const resolvedMaxPrecision = Number.isFinite(Number(responsePrecision)) ? Number(responsePrecision) : MAX_PRECISION;

    // Avoids timezone day-boundary rollover
    const getUtcDayBoundary = (value, endOfDay = false) => {
        const date = new Date(value);
        return new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            endOfDay ? 23 : 0,
            endOfDay ? 59 : 0,
            endOfDay ? 59 : 0
        )).toISOString();
    };

    const handleSort = (columnKey) => {
        let direction = SORT_DIRECTION.ASC;
        if (sortConfig.key === columnKey && sortConfig.direction === SORT_DIRECTION.ASC) {
            direction = SORT_DIRECTION.DESC;
        }
        setSortConfig({ key: columnKey, direction });
    };

    const handleFilterChange = useCallback((columnKey, value) => {
        setFilters(prev => ({
            ...prev,
            [columnKey]: value
        }));
    }, []);

    const headerColumns = useMemo(() => [
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.RADIO, title: '', className: 'radio-cell', sortable: false, filterable: false },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP, title: translate('ABB.Powertrain.Frontend.timestampLabel')?.toUpperCase(), className: 'timestamp-cell', sortable: true, filterable: true },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.SPEED, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Speed')?.toUpperCase(), className: 'speed-cell', sortable: true, filterable: true },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Nameplate_LineFrequency')?.toUpperCase(), className: 'linefrequency-cell', sortable: true, filterable: true },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Temperature')?.toUpperCase(), className: 'temperature-cell', sortable: true, filterable: true }
    ], [translate]);

    useEffect(() => {
        if (isShowRawDataCollectionDialog) {
            getRawDataCollectionTimestamps(selectedAsset, getUtcDayBoundary(dateSelection?.from), getUtcDayBoundary(dateSelection?.to, true));
        }
    }, []);

    useEffect(() => {
        if (!isShowRawDataCollectionDialog) {
            setRawDataError(null);
        }
    }, [isShowRawDataCollectionDialog]);

    useEffect(() => {
        if (rawDataCollections?.loadingStatus === apiCallStatus.ERROR) {
            setRawDataError({
                code: rawDataCollections?.error?.response?.status || rawDataCollections?.error?.status || rawDataCollections?.error?.statusCode || 500,
                message: rawDataCollections?.error?.message || translate('ABB.Powertrain.Frontend.noDataLabel')
            });
        }
    }, [rawDataCollections?.loadingStatus]);

    useEffect(() => {
        if (!isShowRawDataCollectionDialog) { return; }

        // Function to attach scroll listener with retry mechanism
        const attachScrollListener = () => {
            const tableContainer = document.querySelector('.raw-data-collection-table .rc-table-content');
            const headerContainer = headerScrollRef?.current;

            if (!tableContainer || !headerContainer) {
                // Retry after a short delay if elements are not found
                const retryTimer = setTimeout(attachScrollListener, 50);
                return () => clearTimeout(retryTimer);
            }

            const handleTableScroll = () => {
                headerContainer.scrollLeft = tableContainer.scrollLeft;
            };

            tableContainer.addEventListener('scroll', handleTableScroll);

            return () => {
                tableContainer.removeEventListener('scroll', handleTableScroll);
            };
        };

        // Start attaching listener after a small initial delay
        const timer = setTimeout(attachScrollListener, 100);

        return () => clearTimeout(timer);
    }, [isShowRawDataCollectionDialog, rawDataCollections?.data?.rawDatas]);

    const handleApplyTimestamp = () => {
        setSelectedRawDataCollection(selectedTimestamp);
        setShowRawDataCollectionDialog(false);
    };

    const handleSelectDate = (from, to) => {
        if (from && to) {
            setRawDataError(null);
            getRawDataCollectionTimestamps(selectedAsset, getUtcDayBoundary(from), getUtcDayBoundary(to, true));
            setSelectedDates({ from, to });
        }
    };

    const filterByField = (data, filterValue, valueGetter) => {
        if (!filterValue || filterValue.trim() === '') {
            return data;
        }
        const searchTerm = filterValue?.toLowerCase().trim();
        return data?.filter(item => {
            const value = valueGetter(item);
            if (value === null || value === undefined) {
                return false;
            }
            const stringValue = value?.toString().toLowerCase().trim();
            return stringValue === searchTerm || stringValue?.includes(searchTerm);
        });
    };

    const convertTemperatureValue = (value, sortKey) => {
        if (sortKey === TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE && unitOfMeasurement === UNIT_OF_MEASUREMENT.IMPERIAL) {
            return value !== null && value !== undefined ? value * 9 / 5 + 32 : null;
        }
        return value;
    };

    const compareValues = (aVal, bVal, sortKey, sortDirection) => {
        if (sortKey === TYPE_CONSTANTS.TIMESTAMP) {
            const dateA = new Date(aVal);
            const dateB = new Date(bVal);
            return sortDirection === SORT_DIRECTION.ASC ? dateA - dateB : dateB - dateA;
        }

        if (typeof aVal === TYPE_CONSTANTS.NUMBER && typeof bVal === TYPE_CONSTANTS.NUMBER) {
            return sortDirection === SORT_DIRECTION.ASC ? aVal - bVal : bVal - aVal;
        }

        return sortDirection === SORT_DIRECTION.ASC
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
    };

    const sortData = (data, sortKey, sortDirection) => {
        return data.sort((a, b) => {
            const aVal = convertTemperatureValue(a[sortKey], sortKey);
            const bVal = convertTemperatureValue(b[sortKey], sortKey);

            const aMissing = aVal === null || aVal === undefined;
            const bMissing = bVal === null || bVal === undefined;

            if (aMissing && bMissing) { return 0; }
            if (aMissing) { return sortDirection === SORT_DIRECTION.ASC ? -1 : 1; }
            if (bMissing) { return sortDirection === SORT_DIRECTION.ASC ? 1 : -1; }

            return compareValues(aVal, bVal, sortKey, sortDirection);
        });
    };

    const getConvertedTemperature = (item) => {
        if (item?.skinTemperature === null || item?.skinTemperature === undefined) {
            return null;
        }
        const convertedTemp = unitOfMeasurement === UNIT_OF_MEASUREMENT.IMPERIAL
            ? item.skinTemperature * 9 / 5 + 32
            : item.skinTemperature;
        return Number.isFinite(Number(convertedTemp)) ? setMaxPrecision(Number(convertedTemp), resolvedMaxPrecision) : convertedTemp;
    };

    const getConvertedSpeed = (item) => {
        if (item?.speed === null || item?.speed === undefined) {
            return null;
        }
        return setMaxPrecision(Number(item.speed), resolvedMaxPrecision);
    };

    const getConvertedLineFrequency = (item) => {
        if (item?.lineFrequency === null || item?.lineFrequency === undefined) {
            return null;
        }
        return setMaxPrecision(Number(item.lineFrequency), resolvedMaxPrecision);
    };

    const normalizeRawData = (rawDatas) => {
        if (!Array.isArray(rawDatas)) {
            return [];
        }
        return rawDatas.map(ts => ({
            timestamp: typeof ts === 'string' ? ts : ts?.timestamp,
            speed: ts?.speed,
            lineFrequency: ts?.lineFrequency,
            skinTemperature: ts?.skinTemperature
        }));
    };

    const columns = useMemo(() => {
        return getColumns(translate, selectedTimestamp, setSelectedTimestamp, unitOfMeasurement, resolvedMaxPrecision);
    }, [translate, selectedTimestamp, unitOfMeasurement, resolvedMaxPrecision]);

    const filteredData = useMemo(() => {
        let data = normalizeRawData(rawDataCollections?.data?.rawDatas);

        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP], (item) =>
            formatDateTime(item?.timestamp)
        );
        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.SPEED], getConvertedSpeed);
        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY], getConvertedLineFrequency);
        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE], getConvertedTemperature);

        // Apply sorting
        if (sortConfig.key) {
            data = sortData(data, sortConfig.key, sortConfig.direction);
        } else {
            data = data?.toSorted((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        }

        return data;
    }, [rawDataCollections?.data?.rawDatas, filters, sortConfig, unitOfMeasurement, resolvedMaxPrecision]);

    return <GeneralDialog
        show={isShowRawDataCollectionDialog}
        size={'xl'}
        close={() => setShowRawDataCollectionDialog(false)}
        title={translate('ABB.Powertrain.Frontend.rawDataCollectionDialogTitle')}
        closeButton={true}
        acceptButtonProps={{
            text: translate('ABB.Powertrain.Frontend.select'),
            onClick: handleApplyTimestamp,
            disabled: !selectedTimestamp || rawDataCollections?.loadingStatus === apiCallStatus.LOADING || !!rawDataError,
        }}
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
            onClick: () => { setShowRawDataCollectionDialog(false); }
        }}
        modalClassName={'raw-data-collection-modal'}
    >
        <LoadingStatusWrapper useCustomAnchor transparent loadingStatus={rawDataCollections?.loadingStatus}>
            {rawDataError ?
                <ErrorView
                    errorCode={rawDataError.code}
                    errorMessage={rawDataError.message}
                    onRetry={() => {
                        setRawDataError(null);
                        getRawDataCollectionTimestamps(selectedAsset, getUtcDayBoundary(dateSelection?.from), getUtcDayBoundary(dateSelection?.to, true));
                    }}
                    onReportIssue={() => { }}
                />
                :
                <>
                    <div className='dialog-header-container'>
                        <label>{translate('ABB.Powertrain.Frontend.date')}</label>
                        <TimeSelectorRow
                            fromDate={dateSelection?.from}
                            toDate={dateSelection?.to}
                            label=''
                            handleSelectDate={handleSelectDate}
                            maxDaysRange={30}
                            maxDaysBack={30}
                            preSelectedDays={7}
                            numberOfMonths={2}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax30DaysNote')}
                        />
                    </div>
                    <TableHeaderRows
                        ref={headerScrollRef}
                        columns={headerColumns}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        translate={translate}
                    />
                    <GeneralTable
                        className='raw-data-collection-table'
                        tableKey='timestamp'
                        columns={columns}
                        data={filteredData}
                        emptyText={translate('ABB.Powertrain.Frontend.noDataLabel')}
                        type='primary'
                        rowClickEvent={(record) => setSelectedTimestamp(record?.timestamp)}
                        scroll={{ x: 900 }}
                    />
                </>
            }
        </LoadingStatusWrapper>
    </GeneralDialog>;
};

RawDataCollectionDialog.propTypes = {
    isShowRawDataCollectionDialog: PropTypes.bool,
    setShowRawDataCollectionDialog: PropTypes.func,
    setSelectedRawDataCollection: PropTypes.func,
    rawDataCollections: PropTypes.object,
    getRawDataCollectionTimestamps: PropTypes.func,
    selectedAsset: PropTypes.object,
    selectedTimestamp: PropTypes.string,
    setSelectedTimestamp: PropTypes.func,
    unitOfMeasurement: PropTypes.string
};

export default RawDataCollectionDialog;
